import { BlockLoading } from './components/fetch-status/block-loading';
import { type RouteItem, routes } from './confs/routes';
import TheMarketLayout from './layouts/market';
import { type FC, type PropsWithChildren, Suspense, isValidElement } from 'react';
import { Route, Switch, matchRoute, useLocation, useRouter } from 'wouter';

// Normalize path by removing duplicate slashes (shared by rendering + fallback
// matching so both walk the exact same patterns).
const joinPath = (parentPath: string, path: string) => `${parentPath}${path}`.replace(/\/+/g, '/');

/**
 * Recursively renders routes with their layouts and nested children
 * @param routes - Array of route configurations
 * @param parentPath - Parent path prefix for nested routes
 * @returns JSX Switch element containing all routes
 *
 * Routes deliberately carry NO per-route Suspense boundary: the single
 * persistent boundary lives in AppRouter. A Suspense boundary freshly mounted
 * during a transition shows its fallback immediately (React only keeps
 * already-revealed content on screen), so per-route boundaries would defeat
 * the transition-navigation retention that useTransitionLocation provides.
 */
const renderRoutes = (routes: RouteItem[], parentPath = ''): JSX.Element => {
  const routeElements = routes.flatMap(route => {
    const { path, component, layout = ({ children }) => <TheMarketLayout>{children}</TheMarketLayout>, children } = route;
    const currentPath = joinPath(parentPath, path);
    const routeElements: JSX.Element[] = [];
    // Recursively render child routes first
    if (children?.length) {
      const childRoutes = renderRoutes(children, currentPath);
      routeElements.push(...(childRoutes.props.children as JSX.Element[]));
    }
    // Create route element
    const RouteElement: FC = () => {
      const Layout = layout as FC<PropsWithChildren>;
      const Component = component as FC;
      return <Layout>{isValidElement(component) ? (component as JSX.Element) : <Component />}</Layout>;
    };
    routeElements.push(<Route key={`route-${currentPath}`} path={currentPath} component={RouteElement} />);
    return routeElements;
  });

  return <Switch>{routeElements}</Switch>;
};

type FlatRoute = Pick<RouteItem, 'fallback' | 'layout'> & { path: string };

// Flattened route table in Switch matching order (children before parent),
// used to pick the cold-load fallback for the path being loaded.
const flattenRoutes = (routes: RouteItem[], parentPath = ''): FlatRoute[] =>
  routes.flatMap(route => {
    const currentPath = joinPath(parentPath, route.path);
    const children = route.children?.length ? flattenRoutes(route.children, currentPath) : [];
    return [...children, { path: currentPath, fallback: route.fallback, layout: route.layout }];
  });

const FLAT_ROUTES = flattenRoutes(routes);

// Fallback of the persistent boundary. It only ever shows on cold/direct
// loads (transitions retain the previous page instead), where it recreates
// what the target route would render: its layout with its skeleton inside.
const RouteFallback: FC = () => {
  const { parser } = useRouter();
  const [path] = useLocation();
  const matched = FLAT_ROUTES.find(route => matchRoute(parser, route.path, path)[0]);
  const Layout = matched?.layout ?? TheMarketLayout;
  return <Layout>{matched?.fallback ?? <BlockLoading />}</Layout>;
};

/**
 * Main application router component
 * @returns Rendered route switch with all application routes
 */
export const AppRouter: FC = () => {
  return <Suspense fallback={<RouteFallback />}>{renderRoutes(routes)}</Suspense>;
};

export default AppRouter;
